<template>
    <div class="guide-page">
        <div class="guide-header">
            <div class="header-inner">
                <div class="header-icon"><i class="el-icon-document"></i></div>
                <div>
                    <h1>数据资产登记流程指南</h1>
                    <p>全程 11 步，按步骤操作即可完成数据资产登记</p>
                </div>
            </div>
        </div>

        <!-- 步骤导航 -->
        <div class="step-nav">
            <div
                v-for="(step, index) in steps"
                :key="index"
                class="step-nav-item"
                :class="{ active: activeStep === index }"
                @click="scrollToStep(index)"
            >
                <span class="step-nav-num">{{ index + 1 }}</span>
                <span class="step-nav-label">{{ step.shortTitle }}</span>
            </div>
        </div>

        <!-- 特别声明 -->
        <div class="notice-bar">
            <i class="el-icon-warning-outline"></i>
            <strong>特别声明：</strong>所有资料需要盖企业公章后上传
        </div>

        <!-- 步骤详情 -->
        <div class="steps-container">
            <div
                v-for="(step, index) in steps"
                :key="index"
                :ref="'step' + index"
                class="step-card"
            >
                <div class="step-card-header">
                    <div class="step-badge">{{ index + 1 }}</div>
                    <div class="step-title-block">
                        <h2>{{ step.title }}</h2>
                        <p class="step-subtitle">{{ step.subtitle }}</p>
                    </div>
                </div>

                <div class="step-card-body">
                    <div class="step-content-left">
                        <div v-if="step.tips && step.tips.length" class="tips-block">
                            <div class="tips-title"><i class="el-icon-info"></i> 操作说明</div>
                            <ul>
                                <li v-for="(tip, ti) in step.tips" :key="ti">{{ tip }}</li>
                            </ul>
                        </div>
                        <div v-if="step.materials && step.materials.length" class="materials-block">
                            <div class="tips-title"><i class="el-icon-paperclip"></i> 需上传材料</div>
                            <ul>
                                <li v-for="(m, mi) in step.materials" :key="mi">{{ m }}</li>
                            </ul>
                        </div>
                        <div v-if="step.warning" class="warning-block">
                            <i class="el-icon-warning"></i> {{ step.warning }}
                        </div>
                        <div v-if="step.downloads && step.downloads.length" class="download-block">
                            <div class="tips-title"><i class="el-icon-download"></i> 模板下载</div>
                            <div v-for="(d, di) in step.downloads" :key="di" class="download-item">
                                <i class="el-icon-document"></i> {{ d }}
                            </div>
                        </div>
                    </div>
                    <div class="step-content-right">
                        <div class="img-wrapper">
                            <img :src="step.img" :alt="step.title" @click="openPreview(step.img, step.title)" />
                            <div class="img-hint"><i class="el-icon-zoom-in"></i> 点击放大</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 图片预览弹窗 -->
        <div v-if="previewVisible" class="img-preview-mask" @click="closePreview">
            <div class="img-preview-box" @click.stop>
                <div class="preview-title">{{ previewTitle }}</div>
                <img :src="previewSrc" />
                <el-button class="preview-close" icon="el-icon-close" circle @click="closePreview"></el-button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'RegistrationGuide',
    data() {
        return {
            activeStep: 0,
            previewVisible: false,
            previewSrc: '',
            previewTitle: '',
            steps: [
                {
                    shortTitle: '平台首页',
                    title: '第一步：进入平台',
                    subtitle: '登录系统，找到数据资产登记入口',
                    img: '/registration-guide/图1.png',
                    tips: [
                        '登录"数据资产全过程AI服务平台"后台系统',
                        '左侧菜单栏点击"数据资产登记"进入登记模块',
                    ],
                },
                {
                    shortTitle: '登记列表',
                    title: '第二步：新增登记',
                    subtitle: '在登记列表页发起新的登记申请',
                    img: '/registration-guide/图2.png',
                    tips: [
                        '进入数据资产登记列表页',
                        '点击页面右上角蓝色"新增登记"按钮，开始填写登记信息',
                    ],
                },
                {
                    shortTitle: '基本信息',
                    title: '第三步：填写基本信息',
                    subtitle: '流程第一步，填写数据资产的基础描述信息',
                    img: '/registration-guide/图3.png',
                    tips: [
                        '按照页面提示依次填写各项基本信息',
                        '包括：资产名称、数据来源、资产类型等字段',
                        '带 * 号的字段为必填项，请务必填写完整',
                    ],
                },
                {
                    shortTitle: '数据属性',
                    title: '第四步：填写数据属性',
                    subtitle: '流程第二步，填写数据的技术属性与分类信息',
                    img: '/registration-guide/图4.png',
                    tips: [
                        '按照页面提示填写数据属性信息',
                        '包括数据类型、数据规模、存储方式等',
                    ],
                    warning: '数据期限即证书有效期，默认为一年，请注意填写准确',
                },
                {
                    shortTitle: '产权信息',
                    title: '第五步：填写产权信息',
                    subtitle: '流程第三步，填写数据资产的权属与期限信息',
                    img: '/registration-guide/图5.png',
                    tips: [
                        '填写数据资产的产权归属信息',
                        '产权期限建议参考以下方式填写：',
                        '①按公司实际情况预估填写',
                        '②或从当前日期起延后30年填写',
                    ],
                },
                {
                    shortTitle: '来源材料',
                    title: '第六步：上传合规来源材料',
                    subtitle: '流程第四步，上传数据资产的合规证明文件',
                    img: '/registration-guide/图6.png',
                    tips: [
                        '上传证明数据来源合规性的相关材料',
                        '以下材料需自行采集，并加盖企业公章后上传',
                    ],
                    materials: [
                        '数据采集合同 / 购买合同',
                        '相关发票',
                        '数据授权书',
                        '其他可证明数据来源合法性的文件',
                    ],
                },
                {
                    shortTitle: '三方存储',
                    title: '第七步：第三方存储与查验报告',
                    subtitle: '如涉及第三方存储，需额外上传协议并下载查验模板',
                    img: '/registration-guide/图7.png',
                    tips: [
                        '若数据存储在第三方平台，需上传第三方存储协议',
                        '需在平台下载"查验报告模板"，填写完成后上传',
                    ],
                    downloads: ['数据资产查验报告模板'],
                },
                {
                    shortTitle: '查验详情表',
                    title: '第八步：下载并填写查验详情表',
                    subtitle: '下载官方模板，按要求填写后上传',
                    img: '/registration-guide/图8.png',
                    tips: [
                        '点击"下载数据资产查验详情表模版"按钮下载模板',
                        '按照表内要求逐项填写完整',
                        '填写完成并加盖公章后上传至对应位置',
                    ],
                    downloads: ['数据资产查验详情表模版'],
                },
                {
                    shortTitle: '登记承诺函',
                    title: '第九步：下载并填写登记承诺函',
                    subtitle: '下载承诺函模板，签字盖章后上传',
                    img: '/registration-guide/图9.png',
                    tips: [
                        '点击"下载数据资产登记承诺函书模版"按钮下载模板',
                        '按照模板要求填写并由法定代表人签字',
                        '加盖企业公章后上传至对应位置',
                    ],
                    downloads: ['数据资产登记承诺函书模版'],
                },
                {
                    shortTitle: '其他文件',
                    title: '第十步：上传其他补充文件（可选）',
                    subtitle: '可根据实际情况上传补充材料，有助于提升审核通过率',
                    img: '/registration-guide/图10.png',
                    tips: [
                        '此部分为可选上传，不影响提交',
                        '建议上传以下材料以提升审核通过率：',
                    ],
                    materials: [
                        '数据安全管理制度文件',
                        '网络安全管理制度文件',
                        '其他与数据资产相关的补充说明材料',
                    ],
                },
                {
                    shortTitle: '确认提交',
                    title: '第十一步：核对信息并提交',
                    subtitle: '最后一步，预览全部填写内容，确认无误后提交',
                    img: '/registration-guide/图11.png',
                    tips: [
                        '系统将展示所有已填写信息的完整预览',
                        '请仔细核对每一项内容是否准确',
                        '确认无误后，点击页面最下方的"确认提交"按钮完成登记',
                    ],
                    warning: '提交后将进入审核流程，请确保所有信息和材料均已填写完整且加盖公章',
                },
            ],
        };
    },
    mounted() {
        window.addEventListener('scroll', this.onScroll);
    },
    beforeDestroy() {
        window.removeEventListener('scroll', this.onScroll);
    },
    methods: {
        scrollToStep(index) {
            const el = this.$refs['step' + index];
            if (el && el[0]) {
                el[0].scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        },
        onScroll() {
            for (let i = this.steps.length - 1; i >= 0; i--) {
                const el = this.$refs['step' + i];
                if (el && el[0]) {
                    const rect = el[0].getBoundingClientRect();
                    if (rect.top <= 200) {
                        this.activeStep = i;
                        break;
                    }
                }
            }
        },
        openPreview(src, title) {
            this.previewSrc = src;
            this.previewTitle = title;
            this.previewVisible = true;
        },
        closePreview() {
            this.previewVisible = false;
        },
    },
};
</script>

<style scoped>
.guide-page {
    min-height: 100vh;
    background: #f4f6f8;
    padding-bottom: 60px;
}

/* Header */
.guide-header {
    background: linear-gradient(135deg, #1565c0 0%, #1976d2 60%, #42a5f5 100%);
    padding: 40px 0 36px;
    color: #fff;
}
.header-inner {
    max-width: 960px;
    margin: 0 auto;
    padding: 0 24px;
    display: flex;
    align-items: center;
    gap: 20px;
}
.header-icon {
    width: 60px;
    height: 60px;
    background: rgba(255,255,255,0.15);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    flex-shrink: 0;
}
.guide-header h1 {
    font-size: 26px;
    font-weight: 700;
    margin: 0 0 6px;
}
.guide-header p {
    font-size: 14px;
    opacity: 0.85;
    margin: 0;
}

/* Step nav */
.step-nav {
    max-width: 960px;
    margin: 20px auto 0;
    padding: 0 24px;
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
}
.step-nav-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 20px;
    background: #fff;
    border: 1px solid #e0e0e0;
    cursor: pointer;
    font-size: 12px;
    color: #666;
    transition: all 0.2s;
}
.step-nav-item:hover {
    border-color: #409EFF;
    color: #409EFF;
}
.step-nav-item.active {
    background: #409EFF;
    border-color: #409EFF;
    color: #fff;
}
.step-nav-num {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: rgba(0,0,0,0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 600;
    flex-shrink: 0;
}
.step-nav-item.active .step-nav-num {
    background: rgba(255,255,255,0.25);
}

/* Notice bar */
.notice-bar {
    max-width: 960px;
    margin: 16px auto 0;
    padding: 10px 16px;
    background: #fff7e6;
    border: 1px solid #ffd591;
    border-radius: 6px;
    color: #d46b08;
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 8px;
}

/* Steps container */
.steps-container {
    max-width: 960px;
    margin: 16px auto 0;
    padding: 0 24px;
    display: flex;
    flex-direction: column;
    gap: 20px;
}

/* Step card */
.step-card {
    background: #fff;
    border-radius: 10px;
    border: 1px solid #e8ecf0;
    overflow: hidden;
}
.step-card-header {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px 20px;
    border-bottom: 1px solid #f0f2f5;
    background: #fafbfc;
}
.step-badge {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #409EFF;
    color: #fff;
    font-size: 16px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}
.step-title-block h2 {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
    margin: 0 0 2px;
}
.step-subtitle {
    font-size: 12px;
    color: #909399;
    margin: 0;
}

/* Step body */
.step-card-body {
    display: flex;
    gap: 24px;
    padding: 20px;
    align-items: flex-start;
}
.step-content-left {
    flex: 1;
    min-width: 0;
}
.step-content-right {
    flex-shrink: 0;
    width: 420px;
}

/* Tips */
.tips-block, .materials-block, .download-block {
    margin-bottom: 14px;
}
.tips-title {
    font-size: 13px;
    font-weight: 600;
    color: #606266;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 5px;
}
.tips-block ul, .materials-block ul {
    margin: 0;
    padding-left: 18px;
}
.tips-block li, .materials-block li {
    font-size: 13px;
    color: #303133;
    line-height: 1.8;
}

/* Warning */
.warning-block {
    background: #fff6f6;
    border: 1px solid #ffc0c0;
    border-radius: 6px;
    padding: 8px 12px;
    color: #e53935;
    font-size: 13px;
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin-bottom: 14px;
}

/* Download */
.download-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    background: #f0f9ff;
    border: 1px solid #d9ecff;
    border-radius: 4px;
    font-size: 13px;
    color: #1565c0;
    margin-bottom: 6px;
}

/* Image */
.img-wrapper {
    position: relative;
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid #e8ecf0;
    cursor: pointer;
}
.img-wrapper img {
    width: 100%;
    display: block;
    transition: transform 0.2s;
}
.img-wrapper:hover img {
    transform: scale(1.01);
}
.img-hint {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0,0,0,0.45);
    color: #fff;
    font-size: 12px;
    text-align: center;
    padding: 5px;
    opacity: 0;
    transition: opacity 0.2s;
}
.img-wrapper:hover .img-hint {
    opacity: 1;
}

/* Preview mask */
.img-preview-mask {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.75);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
}
.img-preview-box {
    position: relative;
    max-width: 90vw;
    max-height: 90vh;
    background: #fff;
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}
.preview-title {
    padding: 12px 48px 12px 16px;
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    border-bottom: 1px solid #eee;
    background: #fafafa;
}
.img-preview-box img {
    max-width: 90vw;
    max-height: calc(90vh - 60px);
    object-fit: contain;
    display: block;
}
.preview-close {
    position: absolute;
    top: 8px;
    right: 8px;
}
</style>
